import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { FaCheck, FaTimes } from "react-icons/fa";

const SummaryTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: "paid_summary.total_value",
    direction: "descending",
  });

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    const cws = data.cws;
    let sortableItems = [...cws];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const aValue = getNestedValue(a, sortConfig.key);
        const bValue = getNestedValue(b, sortConfig.key);

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        // If values are equal, use Address as the tie breaker
        if (a.Address < b.Address) {
          return -1;
        }
        if (a.Address > b.Address) {
          return 1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  function shortenString(str) {
    if (str.length <= 8) {
      return str; // If the string is too short, just return it as is
    }
    return `${str.slice(0, 4)}...${str.slice(-4)}`;
  }

  const formatCoin = (value) => {
    return value
      ? value.toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })
      : value;
  };

  const formatThreshold = (threshold) => {
    if (Array.isArray(threshold) && threshold.length === 2) {
      return `${threshold[0]} / ${threshold[1]}`;
    }
    return "";
  };

  return (
    <div className="overflow-x-auto relative">
      <table className="w-full text-sm text-left text-gray-600">
        <thead className="text-xs uppercase bg-gray-50">
          <tr>
            <th
              onClick={() => requestSort("Address")}
              className="py-2 px-4 cursor-pointer whitespace-nowrap text-center"
            >
              CW Address{" "}
              {sortConfig.key === "Address"
                ? sortConfig.direction === "descending"
                  ? "↓"
                  : "↑"
                : ""}
            </th>
            <th
              onClick={() => requestSort("Name")}
              className="py-2 px-4 cursor-pointer whitespace-nowrap"
            >
              Name{" "}
              {sortConfig.key === "Name"
                ? sortConfig.direction === "descending"
                  ? "↓"
                  : "↑"
                : ""}
            </th>
            <th
              onClick={() => requestSort("is_multi_action")}
              className="py-2 px-4 cursor-pointer whitespace-nowrap text-center"
            >
              Init{" "}
              {sortConfig.key === "is_multi_action"
                ? sortConfig.direction === "descending"
                  ? "↓"
                  : "↑"
                : ""}
            </th>
            <th
              onClick={() => requestSort("threshold")}
              className="py-2 px-4 cursor-pointer whitespace-nowrap text-center"
            >
              Threshold{" "}
              {sortConfig.key === "threshold"
                ? sortConfig.direction === "descending"
                  ? "↓"
                  : "↑"
                : ""}
            </th>
            <th
              onClick={() => requestSort("paid_summary.payees")}
              className="py-2 px-4 cursor-pointer whitespace-nowrap text-right"
            >
              #Payees{" "}
              {sortConfig.key === "paid_summary.payees"
                ? sortConfig.direction === "descending"
                  ? "↓"
                  : "↑"
                : ""}
            </th>
            <th
              onClick={() => requestSort("paid_summary.total_value")}
              className="py-2 px-4 cursor-pointer whitespace-nowrap text-right"
            >
              Payments{" "}
              {sortConfig.key === "paid_summary.total_value"
                ? sortConfig.direction === "descending"
                  ? "↓"
                  : "↑"
                : ""}
            </th>
            <th
              onClick={() => requestSort("balance.total")}
              className="py-2 px-4 cursor-pointer whitespace-nowrap text-right"
            >
              Balance{" "}
              {sortConfig.key === "balance.total"
                ? sortConfig.direction === "descending"
                  ? "↓"
                  : "↑"
                : ""}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((wallet, index) => (
            <tr
              key={index}
              style={{ borderColor: "transparent" }}
              className={`border-b ${
                (index + 1) % 2 === 0 ? "bg-gray-50" : "bg-white"
              }`}
            >
              <td className="py-2 px-4 text-center">
                {shortenString(wallet.Address)}
              </td>
              <td className="py-2 px-4">{wallet.Name}</td>
              <td className="py-2 px-4 text-center">
                {wallet.is_multi_action ? (
                  <FaCheck className="text-green-500" />
                ) : (
                  <FaTimes className="text-red-500" />
                )}
              </td>
              <td className="py-2 px-4 text-center">
                {formatThreshold(wallet.threshold)}
              </td>
              <td className="py-2 px-4 text-right">
                {wallet.paid_summary.payees}
              </td>
              <td className="py-2 px-4 text-right">
                {formatCoin(wallet.paid_summary.total_value / 1000000)}
              </td>
              <td className="py-2 px-4 text-right">
                {formatCoin(wallet.balance.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

SummaryTable.propTypes = {
  data: PropTypes.object.isRequired,
};

export default SummaryTable;
